import i18n from "i18next";

import { BasicCustomerData, Basket } from "@/services/model";
import { isTrialCheckout } from "@/utils/basketUtils";
import { mobileAbos, ServiceIDs } from "@/utils/constants";
import { Duration, TransformedProduct } from "@/utils/tariffUtils";

const isPortal = (url: string) => url.startsWith("/#/portal");

// Helper function to determine item_category2 based on product ID
const getItemCategory2 = (productId?: number): string => {
  if (!productId) return "flat_data";

  // Check if it's a Flat Mobile tariff (including Swiss and Plus variants)
  if (mobileAbos.includes(productId.toString())) {
    return "flat_mobile";
  }

  // Check if it's a Flat Home tariff
  if (
    productId === ServiceIDs.FlatHome4G ||
    productId === ServiceIDs.FlatHome5G
  ) {
    return "flat_home";
  }

  // Default to flat_data for other tariffs
  return "flat_data";
};

// Helper function to get shop language
const getShopLanguage = (): string => {
  const language = i18n.language?.split("-")[0]?.toUpperCase() || "EN";

  // Return DE, FR, or default to EN
  if (language === "DE" || language === "FR") {
    return language;
  }

  return "EN";
};

const windowPushEvent = (event: any) => {
  console.log("windowPushEvent", event);
};

if (import.meta.env.MODE === "staging" || import.meta.env.DEV) {
  window.dataLayer = {
    push: windowPushEvent,
  } as any;
}

class OnboardingAnalytics {
  private static instance: OnboardingAnalytics;
  private customerType: "b2c" | "b2b" = "b2c";

  private constructor() {}

  public static getInstance(): OnboardingAnalytics {
    if (!OnboardingAnalytics.instance) {
      OnboardingAnalytics.instance = new OnboardingAnalytics();
    }

    return OnboardingAnalytics.instance;
  }

  public setCustomerType(customerType: "b2c" | "b2b") {
    this.customerType = customerType;
  }

  public pushSimTypeSelection(isEsim: boolean) {
    const BUY_AND_ACTIVATE_ESIM = "buy and activate esim";
    const ACTIVATE_SIM_CARD = "activate sim card";

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event_parameter: null,
    });
    window.dataLayer.push({
      event: "custom_event",
      event_name: "connect - sim type",
      event_parameter: {
        customer_type: this.customerType,
        action: "select sim type",
        page_location: window.location.href,
        link_url: isPortal(window.location.href)
          ? `/portal/add-device/2/device-type`
          : `/onboarding/2/device-type`,
        selected_option: isEsim ? BUY_AND_ACTIVATE_ESIM : ACTIVATE_SIM_CARD,
      },
    });
  }

  public pushSimActivation() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event_parameter: null,
    });
    window.dataLayer.push({
      event: "custom_event",
      event_name: "connect - sim type",
      event_parameter: {
        customer_type: this.customerType,
        action: "activate sim card",
        page_location: window.location.href,
        link_url:
          window.location.origin +
          window.location.pathname +
          "#/portal/add-device/2/device-type",
      },
    });
  }

  public pushDeviceTypeSelection(deviceType: string) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event_parameter: null,
    });
    window.dataLayer.push({
      event: "custom_event",
      event_name: "connect - device type",
      event_parameter: {
        customer_type: this.customerType,
        action: "select device type",
        page_location: window.location.href,
        link_url:
          window.location.origin +
          window.location.pathname +
          isPortal(window.location.href)
            ? `/portal/3/rate-selection`
            : `/onboarding/3/rate-selection`,
        selected_option: deviceType,
      },
    });
  }

  public pushProductImpression(
    products: TransformedProduct[],
    itemListName: string,
  ) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event_parameter: null,
    });
    window.dataLayer.push({
      event: "view_item_list",
      ecommerce: {
        item_list_id: "connect_tariff_product_list",
        item_list_name: `connect tariff product list|/${itemListName}/`,
        items: products.map((product) => ({
          item_id: product.id,
          item_name: product.shortName,
          affiliation: "connect.digitalrepublic.ch",
        })),
      },
    });
  }

  public pushProductClick(
    product: TransformedProduct,
    isEsim: boolean,
    customerType: "b2c" | "b2b",
    basket?: Basket,
  ) {
    const analyticsData = {
      event: "select_item",
      ecommerce: {
        customer_type: customerType,
        item_list_id: "connect_tariff_product_list",
        item_list_name: "connect tariff product list|/rate-selection/",
        items: [
          {
            item_id: product.id,
            item_name: product.shortName,
            affiliation: "connect.digitalrepublic.ch",
            item_brand: "digital republic",
            item_category: isEsim ? "esim" : "sim",
            item_category2: getItemCategory2(product.id),
            item_category4:
              product.duration === Duration.month ? "month" : "year",
            item_category5:
              basket && isTrialCheckout(basket) ? "trial" : "paid",
            item_variant: product.shortName,
            item_list_id: "connect_tariff_product_list",
            item_list_name: "connect tariff product list|/rate-selection/",
            quantity: 1,
            price: product.chargeFlat,
            bandwidth: product.bandwidthLabel,
            duration: product.duration,
            base_display_value: product.baseDisplayValue,
          },
        ],
      },
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(analyticsData);
  }

  public enableNumberPorting(
    previousProvider: string,
    previousContractType: "subscription" | "prepaid",
    cancellation: "after end of contract" | "early cancellation",
  ) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event_parameter: null,
    });
    window.dataLayer.push({
      event: "custom_event",
      event_name: "connect - tariff selection",
      event_parameter: {
        customer_type: this.customerType,
        action: "enable number porting",
        page_location: window.location.href,
        link_url:
          window.location.origin +
          window.location.pathname +
          isPortal(window.location.href)
            ? `/portal/4/personal-details`
            : `/onboarding/4/personal-details`,
        previous_provider: previousProvider,
        previous_contracty_type: previousContractType,
        cancellation: cancellation,
      },
    });
  }

  public pushTariffSelection(
    selectedOption: string,
    numberPorting: boolean,
    product?: TransformedProduct,
    basket?: Basket,
    isEsim?: boolean,
  ) {
    const analyticsData = {
      event: "custom_event",
      event_name: "connect - tariff selection",
      event_parameter: {
        customer_type: this.customerType,
        action: "select tariff",
        page_location: window.location.href,
        link_url:
          window.location.origin +
          window.location.pathname +
          isPortal(window.location.href)
            ? `/portal/3/rate-selection`
            : `/onboarding/3/rate-selection`,
        selected_option: selectedOption,
        number_porting: numberPorting.toString(),
        // Add item categories if product is provided
        ...(product && {
          item_id: product.id,
          item_name: product.shortName,
          item_category: isEsim ? "esim" : "sim",
          item_category2: getItemCategory2(product.id),
          item_category4:
            product.duration === Duration.month ? "month" : "year",
          item_category5: basket && isTrialCheckout(basket) ? "trial" : "paid",
        }),
      },
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event_parameter: null,
    });
    window.dataLayer.push(analyticsData);
  }

  public pushPersonalData(
    target: string,
    loginStatus?: "existing account" | "new account",
  ) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event_parameter: null,
    });
    const event = {
      event: "custom_event",
      event_name: "connect - personal data",
      event_parameter: {
        customer_type: this.customerType,
        action: "successfully submitted",
        page_location: window.location.href,
        link_url: target,
        login_status: loginStatus,
      },
    };
    if (loginStatus) {
      event.event_parameter.login_status = loginStatus;
    }

    window.dataLayer.push(event);
  }

  public pushPaymentInfo(
    product: TransformedProduct,
    basket: Basket,
    promotionId: string,
    promotionName: string,
    autorenewalOn: boolean,
    isEsim: boolean,
  ) {
    const analyticsData = {
      event: "add_payment_info",
      ecommerce: {
        customer_type: this.customerType,
        currency: basket.currency?.description,
        value: basket.total?.oneTime?.amount,
        coupon: basket.promotionalCode?.promotionalCode,
        discount: basket.total?.oneTime?.amountDiscounts,
        items: [
          {
            item_id: product.id,
            item_name: product.shortName,
            affiliation: "connect.digitalrepublic.ch",
            coupon: basket.promotionalCode?.promotionalCode,
            discount: basket.total?.oneTime?.amountDiscounts,
            item_brand: "digital republic",
            item_category: isEsim ? "esim" : "sim",
            item_category2: getItemCategory2(product.id),
            item_category4:
              product.duration === Duration.month ? "month" : "year",
            item_category5: isTrialCheckout(basket) ? "trial" : "paid",
            item_variant: product.shortName,
            item_list_id: "mobile_product_list",
            item_list_name: "product list|/mobile/",
            index: 0,
            promotion_id: promotionId,
            promotion_name: promotionName,
            quantity: 1,
            price: basket.total?.oneTime?.amount,
            shop_language: getShopLanguage(),
            auto_renewal_preference: autorenewalOn
              ? "auto renewal"
              : "one time payment",
            bandwidth: product.bandwidthLabel,
            duration: product.duration,
            base_display_value: product.baseDisplayValue,
          },
        ],
      },
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push(analyticsData);
  }

  public pushPurchase(
    product: TransformedProduct,
    customerData: BasicCustomerData,
    basket: Basket,
    promotionId: string,
    promotionName: string,
    autorenewalOn: boolean,
    transactionId: number,
    isEsim: boolean,
  ) {
    const analyticsData = {
      event: "purchase",
      ecommerce: {
        transaction_id: transactionId,
        customer_type: this.customerType,
        value: basket.total?.oneTime,
        currency: basket.currency?.description,
        coupon: basket.promotionalCode?.promotionalCode,
        discount: basket.total?.oneTime?.amountDiscounts,
        items: [
          {
            item_id: product.id,
            item_name: product.shortName,
            affiliation: "connect.digitalrepublic.ch",
            coupon: basket.promotionalCode?.promotionalCode,
            discount: basket.total?.oneTime?.amountDiscounts,
            item_brand: "digital republic",
            item_category: isEsim ? "esim" : "sim",
            item_category2: getItemCategory2(product.id),
            item_category4:
              product.duration === Duration.month ? "month" : "year",
            item_category5: isTrialCheckout(basket) ? "trial" : "paid",
            item_variant: product.shortName,
            item_list_id: "mobile_product_list",
            item_list_name: "product list|/mobile/",
            index: 0,
            promotion_id: promotionId,
            promotion_name: promotionName,
            quantity: 1,
            price: basket.total?.oneTime?.amount,
            shop_language: getShopLanguage(),
            auto_renewal_preference: autorenewalOn
              ? "auto renewal"
              : "one time payment",
            bandwidth: product.bandwidthLabel,
            duration: product.duration,
            base_display_value: product.baseDisplayValue,
          },
        ],
      },
      user_data: {
        first_name: customerData.firstName,
        last_name: customerData.lastName,
        email: customerData.email,
        phone: customerData.phone?.phone,
        city: customerData.mainAddress?.city,
        zip_code: customerData.mainAddress?.zip,
        country: customerData.mainAddress?.country,
      },
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push(analyticsData);
  }
}

export const onboardingAnalytics = OnboardingAnalytics.getInstance();
